/**
 * Vega-Lite spec builders and the statistics behind the analytics charts.
 *
 * Statistical ground rules (see lib/stats.ts):
 * - Spearman (rank) correlation, never Pearson — extractor scores are ordinal.
 * - Series are first-differenced before correlating so that two scores which
 *   merely both drift over treatment don't fake-correlate.
 * - Permutation p-values; Benjamini-Hochberg FDR when many pairs are tested.
 * - Compositional data (legacy "stacked" extractors whose shares sum to 100%)
 *   is excluded from correlation analyses: closure forces spurious negative
 *   correlation between shares.
 * - Minimum 8 sessions for any correlation/trend claim; n is always reported.
 */
import type {
  ChartType,
  ChartHint,
  TopicDistributionRow,
  OuraDailyRow,
  SessionMetricsRow,
} from "../types/charts";
import {
  spearmanPermutationP,
  benjaminiHochberg,
  theilSen,
  mannKendall,
  rmssd,
  firstDifferences,
  mean,
  lag1Autocorrelation,
  variance,
  suddenShifts,
} from "./stats";

// Extended row type with extractor info
export interface ExtractorRow extends TopicDistributionRow {
  extractor: string;
  extractorHint: ChartHint;
  /** evidence quotes joined for tooltip display */
  evidenceText?: string;
}

/** Minimum sessions before a correlation/trend statistic is reported. */
export const MIN_SESSIONS = 8;
/** FDR threshold for calling a result significant. */
export const FDR_ALPHA = 0.05;
const PERMUTATIONS = 2000;
const PERM_SEED = 1234;

/** Why an analysis came back empty — drives the user-facing message. */
export type AnalysisReason =
  | "ok"
  | "not_enough_sessions"
  | "compositional_only"
  | "no_data"
  | "missing_inputs";

export interface CorrelationResult {
  topic: string;
  rho: number;
  p: number;
  q: number;
  n: number;
  significant: boolean;
}

export interface TrendResult {
  topic: string;
  slopePerWeek: number;
  p: number;
  q: number;
  n: number;
  significant: boolean;
}

export interface InstabilityResult {
  topic: string;
  rmssd: number;
  mean: number;
  n: number;
}

export interface CooccurrenceResult {
  topicA: string;
  topicB: string;
  rho: number;
  p: number;
  q: number;
  n: number;
  significant: boolean;
}

export interface Analysis<T> {
  results: T[];
  reason: AnalysisReason;
  /** Largest usable session count seen — for "have 5, need 8" messages. */
  maxN: number;
}

// Base Vega-Lite config (shared across all charts)
const BASE_VL = {
  $schema: "https://vega.github.io/schema/vega-lite/v6.json",
  background: "transparent",
  autosize: { type: "fit", contains: "padding" },
  config: {
    view: { stroke: null },
    font: "'Albert Sans', sans-serif",
    axis: {
      labelColor: "#2b2b33",
      titleColor: "#8a8794",
      titleFontWeight: 600,
      gridColor: "#efeae0",
      tickColor: "#d6cfc0",
      domainColor: "#d6cfc0",
    },
    legend: {
      labelColor: "#2b2b33",
      titleColor: "#8a8794",
      orient: "top" as const,
      symbolType: "circle" as const,
    },
    bar: { cornerRadiusEnd: 4 },
    range: {
      // Validated against scripts/validate_palette.js on the light chart
      // surface (#fcfcfb): lightness band PASS, chroma floor PASS, CVD
      // separation PASS (worst adjacent ΔE 26.8 protan). Gold is a
      // contrast WARN at 2.97:1 (gate still passes) — never use gold as
      // the sole differentiator; pair it with a secondary encoding
      // (legend, position, or — as in the markers small multiples — a
      // single-hue-per-row layout that never relies on gold alone).
      category: [
        "#4f5d9e", // brand indigo
        "#c08a1f", // accent gold (was #b9892e, chroma raised)
        "#2e8a44", // green (was sage #5f7d63, chroma raised)
        "#9c55c0", // plum (was #8a6d9e, chroma raised)
        "#c14a44", // rose (was #b3524d, chroma raised)
        "#1287ab", // teal (was slate #5d8a9e, chroma raised)
        "#d0641f", // copper (replaces rail-navy #23253a — failed lightness+chroma)
        "#8a8794", // muted (overflow/"other" — unchanged)
      ],
    },
  },
} as const;

interface ChartDimensions {
  width: number;
  height: number;
}

const NON_SIGNIFICANT_COLOR = "#d6cfc0";

/**
 * Build a message spec (for loading/error/empty states)
 */
export function buildMessageSpec(msg: string, dims: ChartDimensions) {
  return {
    ...BASE_VL,
    width: dims.width,
    height: 120,
    data: { values: [{ _: 1 }] },
    mark: { type: "text", fontSize: 14, color: "#8a8794" },
    encoding: {
      x: { value: dims.width / 2 },
      y: { value: 60 },
      text: { value: msg },
    },
  };
}

/* ============================================================
   Series shaping helpers
   ============================================================ */

interface SeriesPoint {
  date: string;
  value: number;
}

/**
 * topic → date-sorted series. Multiple rows for the same (topic, date)
 * (e.g. same topic name from two extractors) are averaged.
 */
function topicSeries(rows: ExtractorRow[]): Map<string, SeriesPoint[]> {
  const acc = new Map<string, Map<string, { sum: number; n: number }>>();
  for (const r of rows) {
    if (!r.date) continue;
    let dates = acc.get(r.topic);
    if (!dates) {
      dates = new Map();
      acc.set(r.topic, dates);
    }
    const a = dates.get(r.date) || { sum: 0, n: 0 };
    a.sum += r.percent;
    a.n += 1;
    dates.set(r.date, a);
  }
  const out = new Map<string, SeriesPoint[]>();
  acc.forEach((dates, topic) => {
    const pts = Array.from(dates.entries())
      .map(([date, a]) => ({ date, value: a.sum / a.n }))
      .sort((a, b) => a.date.localeCompare(b.date));
    out.set(topic, pts);
  });
  return out;
}

/** Align two series on their common dates, preserving date order. */
function alignByDate(
  a: SeriesPoint[],
  b: SeriesPoint[]
): { xs: number[]; ys: number[] } {
  const bByDate = new Map(b.map((p) => [p.date, p.value]));
  const xs: number[] = [];
  const ys: number[] = [];
  for (const p of a) {
    const v = bByDate.get(p.date);
    if (v !== undefined) {
      xs.push(p.value);
      ys.push(v);
    }
  }
  return { xs, ys };
}

/** Rows usable for correlation analyses: independent scales only.
 * (airtime is an independent 0-100% per theme — not compositional) */
function nonCompositionalRows(rows: ExtractorRow[]): ExtractorRow[] {
  return rows.filter(
    (r) =>
      r.extractorHint === "simple" ||
      r.extractorHint === "multi" ||
      r.extractorHint === "airtime" ||
      r.extractorHint === "markers"
  );
}

function hasCompositional(rows: ExtractorRow[]): boolean {
  return rows.some((r) => r.extractorHint === "stacked");
}

/* ============================================================
   Correlation: topic/score series vs each other (detrended Spearman)
   ============================================================ */

/**
 * Correlate each topic series against the wellbeing/biometric score series
 * (or all pairs when only score series are present).
 *
 * Both series are first-differenced on their common dates, then Spearman
 * rho + permutation p is computed, with BH-FDR across all tested pairs.
 */
export function computeCorrelations(rows: ExtractorRow[]): Analysis<CorrelationResult> {
  const usable = nonCompositionalRows(rows);
  if (usable.length === 0) {
    return {
      results: [],
      reason: hasCompositional(rows) ? "compositional_only" : "no_data",
      maxN: 0,
    };
  }

  const scoreRows = usable.filter((r) => r.extractorHint === "simple");
  // markers are a multi-series extractor (one rate per marker type per session),
  // so they belong with topicRows, not the single-series scoreRows above.
  const topicRows = usable.filter(
    (r) => r.extractorHint === "multi" || r.extractorHint === "airtime" || r.extractorHint === "markers"
  );

  let pairs: { label: string; xs: number[]; ys: number[] }[] = [];

  if (topicRows.length > 0 && scoreRows.length > 0) {
    // topic vs score: average all simple scores per date into one series
    const scoreSeriesMap = topicSeries(scoreRows);
    const merged = new Map<string, { sum: number; n: number }>();
    scoreSeriesMap.forEach((pts) => {
      for (const p of pts) {
        const a = merged.get(p.date) || { sum: 0, n: 0 };
        a.sum += p.value;
        a.n += 1;
        merged.set(p.date, a);
      }
    });
    const scoreSeries: SeriesPoint[] = Array.from(merged.entries())
      .map(([date, a]) => ({ date, value: a.sum / a.n }))
      .sort((a, b) => a.date.localeCompare(b.date));

    topicSeries(topicRows).forEach((pts, topic) => {
      const { xs, ys } = alignByDate(pts, scoreSeries);
      pairs.push({ label: topic, xs, ys });
    });
  } else {
    // all-simple (e.g. HRV vs wellbeing) or all-multi: pairwise between series
    const series = topicSeries(usable);
    const names = Array.from(series.keys());
    for (let i = 0; i < names.length; i++) {
      for (let j = i + 1; j < names.length; j++) {
        const { xs, ys } = alignByDate(series.get(names[i])!, series.get(names[j])!);
        pairs.push({ label: `${names[i]} vs ${names[j]}`, xs, ys });
      }
    }
  }

  let maxN = 0;
  const tested: { label: string; rho: number; p: number; n: number }[] = [];
  for (const pair of pairs) {
    const n = pair.xs.length;
    maxN = Math.max(maxN, n);
    if (n < MIN_SESSIONS) continue;
    // detrend: correlate session-to-session changes, not levels
    const dx = firstDifferences(pair.xs);
    const dy = firstDifferences(pair.ys);
    const { rho, p } = spearmanPermutationP(dx, dy, PERMUTATIONS, PERM_SEED);
    if (!Number.isFinite(rho)) continue;
    tested.push({ label: pair.label, rho, p, n });
  }

  if (tested.length === 0) {
    return { results: [], reason: "not_enough_sessions", maxN };
  }

  const qvals = benjaminiHochberg(tested.map((t) => t.p));
  const results: CorrelationResult[] = tested.map((t, i) => ({
    topic: t.label,
    rho: Math.round(t.rho * 100) / 100,
    p: Math.round(t.p * 1000) / 1000,
    q: Math.round(qvals[i] * 1000) / 1000,
    n: t.n,
    significant: qvals[i] < FDR_ALPHA,
  }));
  results.sort((a, b) => b.rho - a.rho);
  return { results, reason: "ok", maxN };
}

/**
 * Diverging bar chart of detrended Spearman correlations.
 * Non-significant (FDR) bars are gray.
 */
export function buildCorrelationSpec(
  data: CorrelationResult[],
  dims: ChartDimensions,
  title?: string
) {
  const sortedTopics = data.map((d) => d.topic);

  return {
    ...BASE_VL,
    width: dims.width,
    height: Math.max(dims.height, data.length * 36),
    data: { values: data },
    layer: [
      {
        mark: { type: "rule", color: "#8a8794", strokeWidth: 1 },
        encoding: { x: { datum: 0, type: "quantitative" } },
      },
      {
        mark: { type: "bar", cornerRadiusEnd: 4, height: 22 },
        encoding: {
          y: {
            field: "topic",
            type: "nominal",
            sort: sortedTopics,
            title: null,
            axis: { labelFontSize: 12, labelFontWeight: 500 },
          },
          x: {
            field: "rho",
            type: "quantitative",
            title: "Spearman ρ of session-to-session changes",
            scale: { domain: [-1, 1] },
            axis: { format: "+.1f", tickCount: 5 },
          },
          color: {
            condition: {
              test: "!datum.significant",
              value: NON_SIGNIFICANT_COLOR,
            },
            field: "rho",
            type: "quantitative",
            scale: {
              domain: [-1, 0, 1],
              range: ["#c14a44", "#f1eee7", "#1287ab"],
            },
            legend: null,
          },
          tooltip: [
            { field: "topic", type: "nominal", title: "Pair" },
            { field: "rho", type: "quantitative", title: "Spearman ρ (detrended)", format: "+.2f" },
            { field: "p", type: "quantitative", title: "p (permutation)", format: ".3f" },
            { field: "q", type: "quantitative", title: "q (FDR-adjusted)", format: ".3f" },
            { field: "n", type: "quantitative", title: "Sessions" },
            { field: "significant", type: "nominal", title: "Significant (q<0.05)" },
          ],
        },
      },
      {
        mark: {
          type: "text",
          align: { expr: "datum.rho >= 0 ? 'left' : 'right'" },
          dx: { expr: "datum.rho >= 0 ? 4 : -4" },
          fontSize: 11,
          fontWeight: 600,
        },
        encoding: {
          y: { field: "topic", type: "nominal", sort: sortedTopics },
          x: { field: "rho", type: "quantitative" },
          text: {
            field: "label",
            type: "nominal",
          },
          color: { value: "#2b2b33" },
        },
        transform: [
          {
            calculate:
              "format(datum.rho, '+.2f') + (datum.significant ? '' : ' (n.s.)') + ' n=' + datum.n",
            as: "label",
          },
        ],
      },
    ],
    title: undefined,
  };
}

/* ============================================================
   Trend: Theil-Sen slope + Mann-Kendall test per topic
   (replaces the old first-half vs second-half "topic shift")
   ============================================================ */

export function computeTopicTrend(rows: ExtractorRow[]): Analysis<TrendResult> {
  const usable = rows.filter(
    (r) =>
      r.extractorHint === "multi" ||
      r.extractorHint === "stacked" ||
      r.extractorHint === "simple" ||
      r.extractorHint === "markers"
  );
  if (usable.length === 0) return { results: [], reason: "no_data", maxN: 0 };

  const series = topicSeries(usable);
  let maxN = 0;
  const tested: { topic: string; slopePerWeek: number; p: number; n: number }[] = [];

  series.forEach((pts, topic) => {
    const n = pts.length;
    maxN = Math.max(maxN, n);
    if (n < MIN_SESSIONS) return;

    const t0 = new Date(pts[0].date).getTime();
    const weeks = pts.map((p) => (new Date(p.date).getTime() - t0) / (7 * 86400_000));
    const values = pts.map((p) => p.value);

    const { slope } = theilSen(weeks, values);
    const mk = mannKendall(values);
    if (!Number.isFinite(slope) || !Number.isFinite(mk.p)) return;
    tested.push({ topic, slopePerWeek: slope, p: mk.p, n });
  });

  if (tested.length === 0) return { results: [], reason: "not_enough_sessions", maxN };

  const qvals = benjaminiHochberg(tested.map((t) => t.p));
  const results: TrendResult[] = tested.map((t, i) => ({
    topic: t.topic,
    slopePerWeek: Math.round(t.slopePerWeek * 100) / 100,
    p: Math.round(t.p * 1000) / 1000,
    q: Math.round(qvals[i] * 1000) / 1000,
    n: t.n,
    significant: qvals[i] < FDR_ALPHA,
  }));
  results.sort((a, b) => b.slopePerWeek - a.slopePerWeek);
  return { results, reason: "ok", maxN };
}

export function buildTopicTrendSpec(
  data: TrendResult[],
  dims: ChartDimensions,
  title?: string
) {
  const sortedTopics = data.map((d) => d.topic);
  const maxAbs = Math.max(0.1, ...data.map((d) => Math.abs(d.slopePerWeek)));

  return {
    ...BASE_VL,
    width: dims.width,
    height: Math.max(dims.height, data.length * 36),
    data: { values: data },
    layer: [
      {
        mark: { type: "rule", color: "#8a8794", strokeWidth: 1 },
        encoding: { x: { datum: 0, type: "quantitative" } },
      },
      {
        mark: { type: "bar", cornerRadiusEnd: 4, height: 22 },
        encoding: {
          y: {
            field: "topic",
            type: "nominal",
            sort: sortedTopics,
            title: null,
            axis: { labelFontSize: 12, labelFontWeight: 500 },
          },
          x: {
            field: "slopePerWeek",
            type: "quantitative",
            title: "Theil-Sen slope (points per week)",
            axis: { format: "+.2f" },
            scale: { domain: [-maxAbs, maxAbs] },
          },
          color: {
            condition: {
              test: "!datum.significant",
              value: NON_SIGNIFICANT_COLOR,
            },
            field: "slopePerWeek",
            type: "quantitative",
            scale: {
              domain: [-maxAbs, 0, maxAbs],
              range: ["#c14a44", "#f1eee7", "#1287ab"],
            },
            legend: null,
          },
          tooltip: [
            { field: "topic", type: "nominal", title: "Topic" },
            { field: "slopePerWeek", type: "quantitative", title: "Slope / week (Theil-Sen)", format: "+.2f" },
            { field: "p", type: "quantitative", title: "p (Mann-Kendall)", format: ".3f" },
            { field: "q", type: "quantitative", title: "q (FDR-adjusted)", format: ".3f" },
            { field: "n", type: "quantitative", title: "Sessions" },
            { field: "significant", type: "nominal", title: "Significant (q<0.05)" },
          ],
        },
      },
      {
        mark: {
          type: "text",
          align: { expr: "datum.slopePerWeek >= 0 ? 'left' : 'right'" },
          dx: { expr: "datum.slopePerWeek >= 0 ? 4 : -4" },
          fontSize: 11,
          fontWeight: 600,
        },
        transform: [
          {
            calculate:
              "format(datum.slopePerWeek, '+.2f') + (datum.significant ? '' : ' (n.s.)')",
            as: "label",
          },
        ],
        encoding: {
          y: { field: "topic", type: "nominal", sort: sortedTopics },
          x: { field: "slopePerWeek", type: "quantitative" },
          text: { field: "label", type: "nominal" },
          color: { value: "#2b2b33" },
        },
      },
    ],
    title: undefined,
  };
}

/* ============================================================
   Co-occurrence: pairwise detrended Spearman + FDR heatmap
   ============================================================ */

export function computeCooccurrence(rows: ExtractorRow[]): Analysis<CooccurrenceResult> {
  const usable = rows.filter(
    (r) => r.extractorHint === "multi" || r.extractorHint === "airtime" || r.extractorHint === "markers"
  );
  if (usable.length === 0) {
    return {
      results: [],
      reason: hasCompositional(rows) ? "compositional_only" : "no_data",
      maxN: 0,
    };
  }

  const series = topicSeries(usable);
  const names = Array.from(series.keys());
  if (names.length < 2) return { results: [], reason: "no_data", maxN: 0 };

  let maxN = 0;
  const tested: { topicA: string; topicB: string; rho: number; p: number; n: number }[] = [];

  for (let i = 0; i < names.length; i++) {
    for (let j = i + 1; j < names.length; j++) {
      const { xs, ys } = alignByDate(series.get(names[i])!, series.get(names[j])!);
      const n = xs.length;
      maxN = Math.max(maxN, n);
      if (n < MIN_SESSIONS) continue;
      const { rho, p } = spearmanPermutationP(
        firstDifferences(xs),
        firstDifferences(ys),
        PERMUTATIONS,
        PERM_SEED
      );
      if (!Number.isFinite(rho)) continue;
      tested.push({ topicA: names[i], topicB: names[j], rho, p, n });
    }
  }

  if (tested.length === 0) return { results: [], reason: "not_enough_sessions", maxN };

  const qvals = benjaminiHochberg(tested.map((t) => t.p));
  const results: CooccurrenceResult[] = tested.map((t, i) => ({
    topicA: t.topicA,
    topicB: t.topicB,
    rho: Math.round(t.rho * 100) / 100,
    p: Math.round(t.p * 1000) / 1000,
    q: Math.round(qvals[i] * 1000) / 1000,
    n: t.n,
    significant: qvals[i] < FDR_ALPHA,
  }));
  results.sort((a, b) => Math.abs(b.rho) - Math.abs(a.rho));
  return { results, reason: "ok", maxN };
}

export function buildCooccurrenceSpec(
  data: CooccurrenceResult[],
  dims: ChartDimensions,
  title?: string
) {
  const topics = new Set<string>();
  data.forEach((d) => {
    topics.add(d.topicA);
    topics.add(d.topicB);
  });
  const topicList = Array.from(topics);

  const matrixData: {
    topicA: string;
    topicB: string;
    rho: number;
    q: number;
    n: number;
    significant: boolean;
    label: string;
    diagonal: boolean;
  }[] = [];

  for (const t of topicList) {
    matrixData.push({
      topicA: t, topicB: t, rho: 1, q: 0, n: 0,
      significant: true, label: "", diagonal: true,
    });
  }
  for (const d of data) {
    const label = d.significant ? d.rho.toFixed(2) : `(${d.rho.toFixed(2)})`;
    matrixData.push({ ...d, label, diagonal: false });
    matrixData.push({
      ...d, topicA: d.topicB, topicB: d.topicA, label, diagonal: false,
    });
  }

  const size = Math.max(dims.height, topicList.length * 44);

  return {
    ...BASE_VL,
    width: size,
    height: size,
    data: { values: matrixData },
    layer: [
      {
        mark: { type: "rect", cornerRadius: 4 },
        encoding: {
          x: {
            field: "topicA",
            type: "nominal",
            sort: topicList,
            title: null,
            axis: { labelAngle: -45, labelFontSize: 11 },
          },
          y: {
            field: "topicB",
            type: "nominal",
            sort: topicList,
            title: null,
            axis: { labelFontSize: 11 },
          },
          color: {
            condition: {
              test: "!datum.significant && !datum.diagonal",
              value: "#f5f2ec",
            },
            field: "rho",
            type: "quantitative",
            scale: {
              domain: [-1, 0, 1],
              range: ["#c14a44", "#faf8f4", "#1287ab"],
            },
            legend: { title: "ρ (detrended)" },
          },
          tooltip: [
            { field: "topicA", type: "nominal", title: "Topic A" },
            { field: "topicB", type: "nominal", title: "Topic B" },
            { field: "rho", type: "quantitative", title: "Spearman ρ (detrended)", format: "+.2f" },
            { field: "q", type: "quantitative", title: "q (FDR-adjusted)", format: ".3f" },
            { field: "n", type: "quantitative", title: "Sessions" },
            { field: "significant", type: "nominal", title: "Significant (q<0.05)" },
          ],
        },
      },
      {
        mark: { type: "text", fontSize: 10, fontWeight: 500 },
        encoding: {
          x: { field: "topicA", type: "nominal", sort: topicList },
          y: { field: "topicB", type: "nominal", sort: topicList },
          text: { field: "label", type: "nominal" },
          color: {
            condition: {
              test: "abs(datum.rho) > 0.5 && datum.significant",
              value: "#fff",
            },
            value: "#8a8794",
          },
        },
      },
    ],
    title: undefined,
  };
}

/* ============================================================
   Instability: RMSSD of successive sessions per topic
   (replaces SD "volatility" — a steady trend is not instability)
   ============================================================ */

export function computeInstability(rows: ExtractorRow[]): Analysis<InstabilityResult> {
  const usable = rows.filter(
    (r) =>
      r.extractorHint === "multi" ||
      r.extractorHint === "stacked" ||
      r.extractorHint === "simple" ||
      r.extractorHint === "markers"
  );
  if (usable.length === 0) return { results: [], reason: "no_data", maxN: 0 };

  const series = topicSeries(usable);
  let maxN = 0;
  const results: InstabilityResult[] = [];

  series.forEach((pts, topic) => {
    const n = pts.length;
    maxN = Math.max(maxN, n);
    if (n < 3) return;
    const values = pts.map((p) => p.value);
    const v = rmssd(values);
    if (!Number.isFinite(v)) return;
    results.push({
      topic,
      rmssd: Math.round(v * 10) / 10,
      mean: Math.round(mean(values) * 10) / 10,
      n,
    });
  });

  if (results.length === 0) return { results: [], reason: "not_enough_sessions", maxN };
  results.sort((a, b) => b.rmssd - a.rmssd);
  return { results, reason: "ok", maxN };
}

export function buildInstabilitySpec(
  data: InstabilityResult[],
  dims: ChartDimensions,
  title?: string
) {
  const sortedTopics = data.map((d) => d.topic);
  const maxV = Math.max(...data.map((d) => d.rmssd), 0.1);

  return {
    ...BASE_VL,
    width: dims.width,
    height: Math.max(dims.height, data.length * 36),
    data: { values: data },
    layer: [
      {
        mark: { type: "bar", cornerRadiusEnd: 4, height: 22 },
        encoding: {
          y: {
            field: "topic",
            type: "nominal",
            sort: sortedTopics,
            title: null,
            axis: { labelFontSize: 12, labelFontWeight: 500 },
          },
          x: {
            field: "rmssd",
            type: "quantitative",
            title: "Session-to-session instability (RMSSD)",
            scale: { domain: [0, Math.ceil(maxV * 1.2)] },
          },
          color: {
            field: "rmssd",
            type: "quantitative",
            scale: { domain: [0, maxV], range: ["#c5cce8", "#b3524d"] },
            legend: null,
          },
          tooltip: [
            { field: "topic", type: "nominal", title: "Topic" },
            { field: "rmssd", type: "quantitative", title: "RMSSD (successive differences)", format: ".1f" },
            { field: "mean", type: "quantitative", title: "Mean level", format: ".1f" },
            { field: "n", type: "quantitative", title: "Sessions" },
          ],
        },
      },
      {
        mark: { type: "text", align: "left", dx: 4, fontSize: 11, fontWeight: 600 },
        encoding: {
          y: { field: "topic", type: "nominal", sort: sortedTopics },
          x: { field: "rmssd", type: "quantitative" },
          text: { field: "rmssd", type: "quantitative", format: ".1f" },
          color: { value: "#2b2b33" },
        },
      },
    ],
    title: undefined,
  };
}

/* ============================================================
   Timeline (stacked/line/multi) — unchanged rendering logic
   ============================================================ */

/**
 * Helper to compute ranked topics
 */
function computeRankedTopics(data: Array<{ topic: string; share: number }>) {
  const acc = new Map<string, { sum: number; n: number }>();
  for (const r of data) {
    const a = acc.get(r.topic) || { sum: 0, n: 0 };
    a.sum += r.share;
    a.n += 1;
    acc.set(r.topic, a);
  }
  return Array.from(acc.entries())
    .sort((a, b) => b[1].sum / b[1].n - a[1].sum / a[1].n)
    .map(([k]) => k);
}

/**
 * Small-multiples ("rows") renderer shared by every hint that can be forced
 * into row layout: one panel per topic, ranked order, row title
 * "{topic} — {last value}", unit label shown once (middle row) rather than
 * on every row. `sharedDomain` clamps every row to the same y-range (bounded
 * hints: multi [0,10], airtime [0,100]); omit it for unbounded hints
 * (markers) so each row keeps an independent scale.
 */
function buildRowsSpec(
  extRows: ExtractorRow[],
  ranked: string[],
  dims: ChartDimensions,
  opts: {
    unitLabel: string;
    topicFieldTitle: string;
    valueTitle: string;
    valueFormat: string;
    sharedDomain?: [number, number];
  }
) {
  const series = topicSeries(extRows);
  const rowTopics = ranked.filter((topic) => series.has(topic));
  const rowCount = Math.max(1, rowTopics.length);
  // Denser than buildPhysioSpec's 90px floor (6 marker rows vs 4 physio
  // panels), not a copy-paste slip.
  const rowHeight = Math.max(70, Math.floor(dims.height / rowCount));
  const middleIdx = Math.floor((rowTopics.length - 1) / 2);

  const rows = rowTopics.map((topic, i) => {
    const pts = series.get(topic)!.map((p) => ({ ...p, topic }));
    const last = pts[pts.length - 1];
    const rowTitle = `${topic} — ${last.value.toFixed(2)}`;

    return {
      width: dims.width,
      height: rowHeight,
      title: rowTitle,
      data: { values: pts },
      transform: [{ calculate: "toDate(datum.date)", as: "dateObj" }],
      mark: {
        type: "line",
        point: { filled: true, size: 60 },
        interpolate: "monotone",
        color: "#4f5d9e", // slot-1 indigo — single series per row, identity is the row title
      },
      encoding: {
        x: {
          field: "dateObj",
          type: "temporal",
          timeUnit: "yearmonthdate",
          title: "Date",
          scale: { padding: 20 },
        },
        y: {
          field: "value",
          type: "quantitative",
          // Unit shown once, on the middle row, rather than six times.
          title: i === middleIdx ? opts.unitLabel : null,
          ...(opts.sharedDomain ? { scale: { domain: opts.sharedDomain } } : {}),
        },
        tooltip: [
          { field: "topic", type: "nominal", title: opts.topicFieldTitle },
          { field: "value", type: "quantitative", title: opts.valueTitle, format: opts.valueFormat },
          { field: "dateObj", type: "temporal", title: "Date" },
        ],
      },
    };
  });

  return {
    ...BASE_VL,
    width: dims.width,
    vconcat: rows,
    resolve: { scale: { y: "independent" } },
    title: undefined,
  };
}

/**
 * Build chart spec that handles multiple extractors with different hints.
 * - Extractors are placed side by side (xOffset by extractor)
 * - Within each extractor, topics are stacked
 * - Stacked extractors normalize to 100%, simple extractors show actual values
 *
 * `renderOverride` ("rows" | "overlay") lets the caller force Timeline
 * rendering away from the extractor hint's default — see
 * docs/superpowers/specs/2026-07-11-chart-render-control-design.md for the
 * routing table. Hints without a rows/overlay distinction (simple, stacked,
 * assessment) and the multi-extractor side-by-side branch ignore it.
 */
export function buildChartSpec(
  chartType: ChartType,
  rows: ExtractorRow[] | TopicDistributionRow[],
  dims: ChartDimensions,
  title?: string,
  renderOverride?: "rows" | "overlay"
) {
  if (!rows.length) {
    return buildMessageSpec("No data available", dims);
  }

  // Check if we have extractor info
  const hasExtractorInfo = "extractor" in rows[0];

  if (!hasExtractorInfo) {
    // Legacy single-extractor mode - treat as stacked
    const data = rows.map((r) => ({ ...r, share: r.percent / 100 }));
    const ranked = computeRankedTopics(data);
    const top5 = ranked.slice(0, 5);

    return {
      ...BASE_VL,
      width: dims.width,
      height: dims.height,
      data: { values: data },
      transform: [{ calculate: "toDate(datum.date)", as: "dateObj" }],
      mark: { type: "bar" },
      encoding: {
        x: {
          field: "dateObj",
          type: "temporal",
          timeUnit: "yearmonthdate",
          title: "Date",
          scale: { padding: 20 },
        },
        y: {
          field: "share",
          type: "quantitative",
          stack: "normalize",
          title: "Distribution",
          axis: { format: "%" },
        },
        color: {
          field: "topic",
          type: "nominal",
          title: "Topic",
          legend: { values: top5 },
          sort: ranked,
        },
        order: { field: "topic", type: "nominal", sort: ranked },
        tooltip: [
          { field: "topic", type: "nominal", title: "Topic" },
          { field: "percent", type: "quantitative", title: "Percent", format: ".1f" },
          { field: "dateObj", type: "temporal", title: "Date" },
        ],
      },
      title: undefined,
    };
  }

  // Multi-extractor mode
  const extRows = rows as ExtractorRow[];
  const extractors = Array.from(new Set(extRows.map((r) => r.extractor)));
  const isSingleExtractor = extractors.length === 1;

  // Get the hint for single extractor case
  const singleHint = isSingleExtractor ? extRows[0].extractorHint : "stacked";

  const data = extRows.map((r) => ({ ...r, share: r.percent / 100 }));
  const ranked = computeRankedTopics(data);
  const top5 = ranked.slice(0, 5);

  // Single extractor: use its specific hint
  if (isSingleExtractor) {
    if (singleHint === "airtime") {
      if (renderOverride === "rows") {
        // Bounded hint: rows share a y-domain so magnitudes stay comparable.
        return buildRowsSpec(extRows, ranked, dims, {
          unitLabel: "% of session",
          topicFieldTitle: "Theme",
          valueTitle: "Airtime %",
          valueFormat: ".1f",
          sharedDomain: [0, 100],
        });
      }
      // Theme airtime: independent 0-100% lines per theme
      return {
        ...BASE_VL,
        width: dims.width,
        height: dims.height,
        data: { values: data },
        transform: [{ calculate: "toDate(datum.date)", as: "dateObj" }],
        mark: { type: "line", point: { filled: true, size: 60 }, interpolate: "monotone" },
        encoding: {
          x: {
            field: "dateObj",
            type: "temporal",
            timeUnit: "yearmonthdate",
            title: "Date",
            scale: { padding: 20 },
          },
          y: {
            field: "percent",
            type: "quantitative",
            title: "Airtime (% of session)",
            scale: { domain: [0, 100] },
          },
          color: {
            field: "topic",
            type: "nominal",
            title: "Theme",
            legend: { values: top5 },
            sort: ranked,
          },
          tooltip: [
            { field: "topic", type: "nominal", title: "Theme" },
            { field: "percent", type: "quantitative", title: "Airtime %", format: ".1f" },
            { field: "dateObj", type: "temporal", title: "Date" },
          ],
        },
        title: undefined,
      };
    }
    if (singleHint === "markers") {
      if (renderOverride === "overlay") {
        // Pre-uplift markers rendering (byte-equivalent form, recovered from
        // commit 3b639d1^ — the uplift replaced this with the rows small
        // multiples below). Single panel, multi-line, unbounded y — no
        // domain clamp, so negative rows (e.g. session-arc) render safely.
        return {
          ...BASE_VL,
          width: dims.width,
          height: dims.height,
          data: { values: data },
          transform: [{ calculate: "toDate(datum.date)", as: "dateObj" }],
          mark: { type: "line", point: { filled: true, size: 60 }, interpolate: "monotone" },
          encoding: {
            x: {
              field: "dateObj",
              type: "temporal",
              timeUnit: "yearmonthdate",
              title: "Date",
              scale: { padding: 20 },
            },
            y: {
              field: "percent",
              type: "quantitative",
              title: "Rate (per 1,000 client words)",
            },
            color: {
              field: "topic",
              type: "nominal",
              title: "Marker",
              legend: { values: top5 },
              sort: ranked,
            },
            tooltip: [
              { field: "topic", type: "nominal", title: "Marker" },
              { field: "percent", type: "quantitative", title: "per 1,000 words", format: ".2f" },
              { field: "dateObj", type: "temporal", title: "Date" },
            ],
          },
          title: undefined,
        };
      }
      // Language markers: unbounded per-1,000-client-word rates. High-rate
      // markers (self-reference ~15-18/1000) flatten low-rate ones (anger
      // 0-5/1000) on a shared axis, so render as small multiples — one row
      // per marker, independent y, ordered most-active first (same `ranked`
      // ordering used elsewhere). Mirrors buildPhysioSpec's vconcat pattern.
      // No hardcoded row count: rows follow the topics present in the data
      // (6 for the seeded extractors today, but not assumed). Same for
      // renderOverride === "rows" — already the default for this hint.
      return buildRowsSpec(extRows, ranked, dims, {
        unitLabel: "Rate (per 1,000 client words)",
        topicFieldTitle: "Marker",
        valueTitle: "Rate",
        valueFormat: ".2f",
      });
    }
    if (singleHint === "multi") {
      if (renderOverride === "rows") {
        // Bounded hint: rows share a y-domain so magnitudes stay comparable.
        return buildRowsSpec(extRows, ranked, dims, {
          unitLabel: "0–10 intensity",
          topicFieldTitle: "Topic",
          valueTitle: "Score",
          valueFormat: ".1f",
          sharedDomain: [0, 10],
        });
      }
      // Multi-topic absolute 0-10 scale: line chart, one line per canonical topic
      return {
        ...BASE_VL,
        width: dims.width,
        height: dims.height,
        data: { values: data },
        transform: [{ calculate: "toDate(datum.date)", as: "dateObj" }],
        mark: { type: "line", point: { filled: true, size: 60 }, interpolate: "monotone" },
        encoding: {
          x: {
            field: "dateObj",
            type: "temporal",
            timeUnit: "yearmonthdate",
            title: "Date",
            scale: { padding: 20 },
          },
          y: {
            field: "percent",
            type: "quantitative",
            title: "Presence (0-10)",
            scale: { domain: [0, 10] },
          },
          color: {
            field: "topic",
            type: "nominal",
            title: "Topic",
            legend: { values: top5 },
            sort: ranked,
          },
          tooltip: [
            { field: "topic", type: "nominal", title: "Topic" },
            { field: "percent", type: "quantitative", title: "Score", format: ".1f" },
            { field: "dateObj", type: "temporal", title: "Date" },
          ],
        },
        title: undefined,
      };
    }
    if (singleHint === "simple") {
      // Simple: scalar score over time → line chart with points.
      // Sudden gains/losses (Tang-DeRubeis criteria, scaled to the 0-100
      // range: ≥10 absolute + ≥25% relative + 3-vs-3 t-test) are annotated.
      const shiftMarkers: { date: string; percent: number; shiftType: string }[] = [];
      topicSeries(extRows).forEach((pts) => {
        const values = pts.map((pt) => pt.value);
        for (const sh of suddenShifts(values, 10, false)) {
          shiftMarkers.push({
            date: pts[sh.index].date,
            percent: pts[sh.index].value,
            shiftType: sh.type,
          });
        }
      });

      const lineLayer = {
        mark: { type: "line", point: { filled: true, size: 60 }, interpolate: "monotone" },
        encoding: {
          x: {
            field: "dateObj",
            type: "temporal",
            timeUnit: "yearmonthdate",
            title: "Date",
            scale: { padding: 20 },
          },
          y: {
            field: "percent",
            type: "quantitative",
            title: "Score",
            scale: { domain: [0, 100] },
          },
          color: {
            field: "topic",
            type: "nominal",
            title: "Topic",
            legend: { values: top5 },
            sort: ranked,
          },
          tooltip: [
            { field: "topic", type: "nominal", title: "Topic" },
            { field: "percent", type: "quantitative", title: "Score", format: ".1f" },
            { field: "evidenceText", type: "nominal", title: "Evidence" },
            { field: "dateObj", type: "temporal", title: "Date" },
          ],
        },
      };
      const markerLayer = {
        data: { values: shiftMarkers },
        transform: [{ calculate: "toDate(datum.date)", as: "dateObj" }],
        mark: { type: "text", dy: -16, fontSize: 10.5, fontWeight: 700 },
        encoding: {
          x: { field: "dateObj", type: "temporal", timeUnit: "yearmonthdate" },
          y: { field: "percent", type: "quantitative" },
          text: {
            condition: { test: "datum.shiftType === 'gain'", value: "✦ sudden gain" },
            value: "✦ sudden loss",
          },
          color: {
            condition: { test: "datum.shiftType === 'gain'", value: "#5f7d63" },
            value: "#b3524d",
          },
        },
      };

      return {
        ...BASE_VL,
        width: dims.width,
        height: dims.height,
        data: { values: data },
        transform: [{ calculate: "toDate(datum.date)", as: "dateObj" }],
        layer: shiftMarkers.length > 0 ? [lineLayer, markerLayer] : [lineLayer],
        title: undefined,
      };
    } else {
      // Stacked: normalized stacked bars
      return {
        ...BASE_VL,
        width: dims.width,
        height: dims.height,
        data: { values: data },
        transform: [{ calculate: "toDate(datum.date)", as: "dateObj" }],
        mark: { type: "bar" },
        encoding: {
          x: {
            field: "dateObj",
            type: "temporal",
            timeUnit: "yearmonthdate",
            title: "Date",
            scale: { padding: 20 },
          },
          y: {
            field: "share",
            type: "quantitative",
            stack: "normalize",
            title: "Distribution",
            axis: { format: "%" },
          },
          color: {
            field: "topic",
            type: "nominal",
            title: "Topic",
            legend: { values: top5 },
            sort: ranked,
          },
          order: { field: "topic", type: "nominal", sort: ranked },
          tooltip: [
            { field: "topic", type: "nominal", title: "Topic" },
            { field: "percent", type: "quantitative", title: "Percent", format: ".1f" },
            { field: "dateObj", type: "temporal", title: "Date" },
          ],
        },
        title: undefined,
      };
    }
  }

  // Multiple extractors: side by side with topics stacked within each extractor
  return {
    ...BASE_VL,
    width: dims.width,
    height: dims.height,
    data: { values: data },
    transform: [{ calculate: "toDate(datum.date)", as: "dateObj" }],
    mark: { type: "bar" },
    encoding: {
      x: {
        field: "dateObj",
        type: "temporal",
        timeUnit: "yearmonthdate",
        title: "Date",
        scale: { padding: 20 },
      },
      xOffset: {
        field: "extractor",
        type: "nominal",
      },
      y: {
        field: "share",
        type: "quantitative",
        stack: true,
        title: "Distribution",
        axis: { format: "%" },
        scale: { domain: [0, 1] },
      },
      color: {
        field: "topic",
        type: "nominal",
        title: "Topic",
        legend: { values: top5 },
        sort: ranked,
      },
      order: { field: "topic", type: "nominal", sort: ranked },
      tooltip: [
        { field: "extractor", type: "nominal", title: "Extractor" },
        { field: "topic", type: "nominal", title: "Topic" },
        { field: "percent", type: "quantitative", title: "Percent", format: ".1f" },
        { field: "dateObj", type: "temporal", title: "Date" },
      ],
    },
    title: undefined,
  };
}

/* ============================================================
   Assessment scores with clinical cutoffs + reliable change (RCI)
   ============================================================ */

export interface InstrumentMeta {
  /** canonical display name */
  name: string;
  max: number;
  /** severity bands: [from, to, label] ascending */
  bands: [number, number, string][];
  /** reliable change index — |Δ| at or above this is a real change */
  rci: number;
  /** true when lower scores are better (symptom scales) */
  lowerIsBetter: boolean;
}

/**
 * Clinical metadata for common validated instruments.
 * Cutoffs follow the published scoring manuals; RCI values are the
 * commonly used reliable-change thresholds from the psychometric
 * literature (PHQ-9 ≥6, GAD-7 ≥4, PCL-5 ≥10, ORS ≥5).
 */
export const INSTRUMENTS: Record<string, InstrumentMeta> = {
  PHQ9: {
    name: "PHQ-9",
    max: 27,
    bands: [
      [0, 4, "minimal"],
      [5, 9, "mild"],
      [10, 14, "moderate"],
      [15, 19, "mod. severe"],
      [20, 27, "severe"],
    ],
    rci: 6,
    lowerIsBetter: true,
  },
  GAD7: {
    name: "GAD-7",
    max: 21,
    bands: [
      [0, 4, "minimal"],
      [5, 9, "mild"],
      [10, 14, "moderate"],
      [15, 21, "severe"],
    ],
    rci: 4,
    lowerIsBetter: true,
  },
  PCL5: {
    name: "PCL-5",
    max: 80,
    bands: [
      [0, 32, "below cutoff"],
      [33, 80, "probable PTSD"],
    ],
    rci: 10,
    lowerIsBetter: true,
  },
  ORS: {
    name: "ORS",
    max: 40,
    bands: [
      [0, 24, "clinical range"],
      [25, 40, "non-clinical"],
    ],
    rci: 5,
    lowerIsBetter: false,
  },
};

export function instrumentMeta(name: string): InstrumentMeta | null {
  const key = name.toUpperCase().replace(/[^A-Z0-9]/g, "");
  return INSTRUMENTS[key] || null;
}

export interface AssessmentPoint {
  date: string;
  instrument: string;
  score: number;
  /** change vs previous administration of the same instrument */
  delta: number | null;
  /** |delta| >= instrument RCI */
  reliableChange: boolean;
  /** "improved" | "worsened" | null (needs instrument direction) */
  direction: string | null;
  /** Tang-DeRubeis sudden gain/loss at this administration */
  suddenShift: "gain" | "loss" | null;
}

export function computeAssessmentSeries(rows: ExtractorRow[]): Analysis<AssessmentPoint> {
  const usable = rows.filter((r) => r.extractorHint === "assessment");
  if (usable.length === 0) return { results: [], reason: "no_data", maxN: 0 };

  const series = topicSeries(usable);
  const results: AssessmentPoint[] = [];
  let maxN = 0;

  series.forEach((pts, instrument) => {
    maxN = Math.max(maxN, pts.length);
    const meta = instrumentMeta(instrument);
    const values = pts.map((p) => p.value);
    const shiftAt = new Map<number, "gain" | "loss">(
      meta
        ? suddenShifts(values, meta.rci, meta.lowerIsBetter).map((sh) => [sh.index, sh.type])
        : []
    );
    let prev: number | null = null;
    pts.forEach((p, idx) => {
      const delta = prev === null ? null : p.value - prev;
      let reliable = false;
      let direction: string | null = null;
      if (delta !== null && meta) {
        reliable = Math.abs(delta) >= meta.rci;
        if (reliable) {
          const improved = meta.lowerIsBetter ? delta < 0 : delta > 0;
          direction = improved ? "improved" : "worsened";
        }
      }
      results.push({
        date: p.date,
        instrument,
        score: p.value,
        delta: delta === null ? null : Math.round(delta * 10) / 10,
        reliableChange: reliable,
        direction,
        suddenShift: shiftAt.get(idx) ?? null,
      });
      prev = p.value;
    });
  });

  if (results.length === 0) return { results: [], reason: "no_data", maxN };
  results.sort((a, b) => a.date.localeCompare(b.date));
  return { results, reason: "ok", maxN };
}

export function buildAssessmentSpec(
  data: AssessmentPoint[],
  dims: ChartDimensions,
  title?: string
) {
  const instruments = Array.from(new Set(data.map((d) => d.instrument)));
  const single = instruments.length === 1 ? instrumentMeta(instruments[0]) : null;

  const layers: any[] = [];

  // Severity bands (only when exactly one known instrument is plotted)
  if (single) {
    const bandData = single.bands.map(([from, to, label]) => ({
      from,
      to: to + 1 <= single.max ? to + 0.999 : single.max,
      band: label,
    }));
    layers.push({
      data: { values: bandData },
      mark: { type: "rect", opacity: 0.07 },
      encoding: {
        y: { field: "from", type: "quantitative" },
        y2: { field: "to" },
        color: { value: "#2b2b33" },
      },
    });
    layers.push({
      data: { values: bandData },
      mark: {
        type: "text",
        align: "right",
        baseline: "top",
        dx: -6,
        dy: 3,
        fontSize: 10,
        color: "#8a8794",
        x: { expr: "width" },
      },
      encoding: {
        y: { field: "to", type: "quantitative" },
        text: { field: "band", type: "nominal" },
      },
    });
  }

  layers.push({
    mark: { type: "line", point: false, interpolate: "monotone", strokeWidth: 2 },
    encoding: {
      x: {
        field: "dateObj",
        type: "temporal",
        timeUnit: "yearmonthdate",
        title: "Date",
      },
      y: {
        field: "score",
        type: "quantitative",
        title: single ? `${single.name} score` : "Score",
        scale: single ? { domain: [0, single.max] } : undefined,
      },
      color: { field: "instrument", type: "nominal", title: "Instrument" },
    },
  });

  // Points: reliable changes get a ring and direction color
  layers.push({
    mark: { type: "point", filled: true, size: 90 },
    encoding: {
      x: { field: "dateObj", type: "temporal", timeUnit: "yearmonthdate" },
      y: { field: "score", type: "quantitative" },
      color: {
        condition: [
          { test: "datum.direction === 'improved'", value: "#5f7d63" },
          { test: "datum.direction === 'worsened'", value: "#b3524d" },
        ],
        field: "instrument",
        type: "nominal",
        legend: null,
      },
      size: {
        condition: { test: "datum.reliableChange", value: 220 },
        value: 90,
      },
      tooltip: [
        { field: "instrument", type: "nominal", title: "Instrument" },
        { field: "score", type: "quantitative", title: "Score" },
        { field: "delta", type: "quantitative", title: "Δ vs previous", format: "+.1f" },
        { field: "reliableChange", type: "nominal", title: "Reliable change (≥ RCI)" },
        { field: "direction", type: "nominal", title: "Direction" },
        { field: "dateObj", type: "temporal", title: "Date" },
      ],
    },
  });

  // Tang-DeRubeis sudden gains/losses get an explicit annotation
  layers.push({
    transform: [{ filter: "isValid(datum.suddenShift)" }],
    mark: { type: "text", dy: -16, fontSize: 10.5, fontWeight: 700 },
    encoding: {
      x: { field: "dateObj", type: "temporal", timeUnit: "yearmonthdate" },
      y: { field: "score", type: "quantitative" },
      text: {
        condition: { test: "datum.suddenShift === 'gain'", value: "✦ sudden gain" },
        value: "✦ sudden loss",
      },
      color: {
        condition: { test: "datum.suddenShift === 'gain'", value: "#5f7d63" },
        value: "#b3524d",
      },
    },
  });

  return {
    ...BASE_VL,
    width: dims.width,
    height: dims.height,
    data: { values: data },
    transform: [{ calculate: "toDate(datum.date)", as: "dateObj" }],
    layer: layers,
    title: undefined,
  };
}

/* ============================================================
   Lead-lag: daily Oura physiology vs session themes at ±N days
   ============================================================ */

export interface LeadLagResult {
  pair: string;
  metric: string;
  topic: string;
  /** days the Oura series is shifted; negative = physiology BEFORE the session */
  lag: number;
  rho: number;
  p: number;
  q: number;
  n: number;
  significant: boolean;
}

const LEADLAG_LAGS = [-3, -2, -1, 0, 1, 2, 3];
/** cap the number of (metric, topic) pairs displayed, ranked by max |rho| */
const LEADLAG_MAX_PAIRS = 6;

function addDays(dateStr: string, days: number): string {
  const d = new Date(dateStr + "T00:00:00Z");
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/**
 * Lagged cross-correlation between daily Oura metrics and session theme
 * scores. For lag L, the theme score on session date D is paired with the
 * Oura value on D+L (so L=-2 asks: did physiology two days BEFORE the
 * session track the theme?). Both series are first-differenced across
 * consecutive sessions before Spearman; FDR across every (pair, lag) test.
 */
export function computeLeadLag(
  rows: ExtractorRow[],
  oura: OuraDailyRow[]
): Analysis<LeadLagResult> {
  const topicRows = rows.filter(
    (r) =>
      r.extractorHint === "multi" ||
      r.extractorHint === "airtime" ||
      r.extractorHint === "simple" ||
      r.extractorHint === "markers"
  );
  if (topicRows.length === 0) return { results: [], reason: "no_data", maxN: 0 };
  if (oura.length === 0) return { results: [], reason: "missing_inputs", maxN: 0 };

  const ouraByMetric = new Map<string, Map<string, number>>();
  for (const r of oura) {
    let m = ouraByMetric.get(r.metric);
    if (!m) {
      m = new Map();
      ouraByMetric.set(r.metric, m);
    }
    m.set(r.date, r.value);
  }

  const themes = topicSeries(topicRows);
  let maxN = 0;
  const tested: Omit<LeadLagResult, "q" | "significant">[] = [];

  themes.forEach((pts, topic) => {
    ouraByMetric.forEach((daily, metric) => {
      for (const lag of LEADLAG_LAGS) {
        const xs: number[] = [];
        const ys: number[] = [];
        for (const p of pts) {
          const v = daily.get(addDays(p.date, lag));
          if (v !== undefined) {
            xs.push(v);
            ys.push(p.value);
          }
        }
        maxN = Math.max(maxN, xs.length);
        if (xs.length < MIN_SESSIONS) continue;
        const { rho, p } = spearmanPermutationP(
          firstDifferences(xs),
          firstDifferences(ys),
          PERMUTATIONS,
          PERM_SEED
        );
        if (!Number.isFinite(rho)) continue;
        tested.push({
          pair: `${metric} → ${topic}`,
          metric,
          topic,
          lag,
          rho: Math.round(rho * 100) / 100,
          p: Math.round(p * 1000) / 1000,
          n: xs.length,
        });
      }
    });
  });

  if (tested.length === 0) return { results: [], reason: "not_enough_sessions", maxN };

  const qvals = benjaminiHochberg(tested.map((t) => t.p));
  let results: LeadLagResult[] = tested.map((t, i) => ({
    ...t,
    q: Math.round(qvals[i] * 1000) / 1000,
    significant: qvals[i] < FDR_ALPHA,
  }));

  // rank pairs by their strongest |rho| and keep the top few for legibility
  const pairStrength = new Map<string, number>();
  for (const r of results) {
    pairStrength.set(r.pair, Math.max(pairStrength.get(r.pair) || 0, Math.abs(r.rho)));
  }
  const keptPairs = new Set(
    Array.from(pairStrength.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, LEADLAG_MAX_PAIRS)
      .map(([pair]) => pair)
  );
  results = results.filter((r) => keptPairs.has(r.pair));

  return { results, reason: "ok", maxN };
}

export function buildLeadLagSpec(
  data: LeadLagResult[],
  dims: ChartDimensions,
  title?: string
) {
  return {
    ...BASE_VL,
    width: dims.width,
    height: dims.height,
    data: { values: data },
    layer: [
      {
        mark: { type: "rule", color: "#8a8794", strokeWidth: 1 },
        encoding: { y: { datum: 0, type: "quantitative" } },
      },
      {
        mark: { type: "bar", cornerRadiusEnd: 3 },
        encoding: {
          x: {
            field: "lag",
            type: "ordinal",
            title: "Lag in days (negative = body leads, positive = body follows)",
            axis: { labelAngle: 0 },
          },
          xOffset: { field: "pair", type: "nominal" },
          y: {
            field: "rho",
            type: "quantitative",
            title: "Spearman ρ (detrended)",
            scale: { domain: [-1, 1] },
          },
          color: { field: "pair", type: "nominal", title: null },
          opacity: {
            condition: { test: "datum.significant", value: 1 },
            value: 0.3,
          },
          tooltip: [
            { field: "pair", type: "nominal", title: "Pair" },
            { field: "lag", type: "quantitative", title: "Lag (days)" },
            { field: "rho", type: "quantitative", title: "Spearman ρ", format: "+.2f" },
            { field: "p", type: "quantitative", title: "p (permutation)", format: ".3f" },
            { field: "q", type: "quantitative", title: "q (FDR)", format: ".3f" },
            { field: "n", type: "quantitative", title: "Paired sessions" },
            { field: "significant", type: "nominal", title: "Significant (q<0.05)" },
          ],
        },
      },
    ],
    title: undefined,
  };
}

/* ============================================================
   Session dynamics: embedding novelty + theme entropy
   ============================================================ */

export function buildDynamicsSpec(
  items: SessionMetricsRow[],
  dims: ChartDimensions,
  title?: string
) {
  const usable = items.filter(
    (i) => i.novelty !== null || i.theme_entropy !== null
  );
  if (usable.length === 0) {
    return buildMessageSpec("No embedding data yet — record sessions to populate this chart", dims);
  }

  return {
    ...BASE_VL,
    width: dims.width,
    height: dims.height,
    data: { values: usable },
    transform: [{ calculate: "toDate(datum.date)", as: "dateObj" }],
    layer: [
      {
        transform: [{ filter: "isValid(datum.novelty)" }],
        mark: {
          type: "line",
          point: { filled: true, size: 70 },
          interpolate: "monotone",
          color: "#4f5d9e",
        },
        encoding: {
          x: { field: "dateObj", type: "temporal", timeUnit: "yearmonthdate", title: "Date" },
          y: {
            field: "novelty",
            type: "quantitative",
            title: "Novelty (cosine distance to previous session)",
            scale: { domain: [0, 1] },
            axis: { titleColor: "#4f5d9e" },
          },
          tooltip: [
            { field: "novelty", type: "quantitative", title: "Novelty", format: ".3f" },
            { field: "theme_entropy", type: "quantitative", title: "Theme entropy (bits)", format: ".2f" },
            { field: "distinct_themes", type: "quantitative", title: "Distinct themes" },
            { field: "dateObj", type: "temporal", title: "Date" },
          ],
        },
      },
      {
        transform: [{ filter: "isValid(datum.theme_entropy)" }],
        mark: {
          type: "line",
          point: { filled: true, size: 70 },
          interpolate: "monotone",
          color: "#b9892e",
          strokeDash: [5, 3],
        },
        encoding: {
          x: { field: "dateObj", type: "temporal", timeUnit: "yearmonthdate" },
          y: {
            field: "theme_entropy",
            type: "quantitative",
            title: "Theme entropy (bits)",
            axis: { titleColor: "#b9892e" },
          },
          tooltip: [
            { field: "theme_entropy", type: "quantitative", title: "Theme entropy (bits)", format: ".2f" },
            { field: "novelty", type: "quantitative", title: "Novelty", format: ".3f" },
            { field: "distinct_themes", type: "quantitative", title: "Distinct themes" },
            { field: "dateObj", type: "temporal", title: "Date" },
          ],
        },
      },
    ],
    resolve: { scale: { y: "independent" } },
    title: undefined,
  };
}

/* ============================================================
   Early-warning signals: critical slowing down
   (rising lag-1 autocorrelation + rising variance in a moving window
   precede state transitions — Olthof et al. 2020, dynamic-systems therapy)
   ============================================================ */

export interface EarlyWarningResult {
  topic: string;
  /** Mann-Kendall z for the rolling AR1 series (positive = rising) */
  ar1Z: number;
  ar1P: number;
  /** Mann-Kendall z for the rolling variance series */
  varZ: number;
  varP: number;
  latestAr1: number;
  n: number;
  /** both signals rising at the warning threshold */
  warning: boolean;
}

const EWS_WINDOW = 5;
/** Warning threshold is deliberately lenient (.10) — this is a watch flag,
 * not a significance claim, per the early-warning-signals literature. */
const EWS_ALPHA = 0.1;

export function computeEarlyWarning(rows: ExtractorRow[]): Analysis<EarlyWarningResult> {
  const usable = nonCompositionalRows(rows);
  if (usable.length === 0) {
    return {
      results: [],
      reason: hasCompositional(rows) ? "compositional_only" : "no_data",
      maxN: 0,
    };
  }

  const series = topicSeries(usable);
  let maxN = 0;
  const results: EarlyWarningResult[] = [];

  series.forEach((pts, topic) => {
    const values = pts.map((p) => p.value);
    const n = values.length;
    maxN = Math.max(maxN, n);
    // rolling stats need EWS_WINDOW points per window and ≥4 windows for MK
    if (n < EWS_WINDOW + 4) return;

    const ar1Series: number[] = [];
    const varSeries: number[] = [];
    for (let end = EWS_WINDOW; end <= n; end++) {
      const win = values.slice(end - EWS_WINDOW, end);
      const a = lag1Autocorrelation(win);
      const v = variance(win);
      if (Number.isFinite(a) && Number.isFinite(v)) {
        ar1Series.push(a);
        varSeries.push(v);
      }
    }
    if (ar1Series.length < 4) return;

    const mkA = mannKendall(ar1Series);
    const mkV = mannKendall(varSeries);
    if (!Number.isFinite(mkA.z) || !Number.isFinite(mkV.z)) return;

    results.push({
      topic,
      ar1Z: Math.round(mkA.z * 100) / 100,
      ar1P: Math.round(mkA.p * 1000) / 1000,
      varZ: Math.round(mkV.z * 100) / 100,
      varP: Math.round(mkV.p * 1000) / 1000,
      latestAr1: Math.round(ar1Series[ar1Series.length - 1] * 100) / 100,
      n,
      warning: mkA.z > 0 && mkV.z > 0 && mkA.p < EWS_ALPHA && mkV.p < EWS_ALPHA,
    });
  });

  if (results.length === 0) return { results: [], reason: "not_enough_sessions", maxN };
  results.sort((a, b) => (b.ar1Z + b.varZ) - (a.ar1Z + a.varZ));
  return { results, reason: "ok", maxN };
}

/** Quadrant scatter: x = AR1 trend, y = variance trend. Top-right = the
 * destabilization quadrant. */
export function buildEarlyWarningSpec(
  data: EarlyWarningResult[],
  dims: ChartDimensions,
  title?: string
) {
  const maxAbs = Math.max(
    1.8,
    ...data.map((d) => Math.max(Math.abs(d.ar1Z), Math.abs(d.varZ)))
  ) * 1.2;

  return {
    ...BASE_VL,
    width: dims.width,
    height: dims.height,
    layer: [
      {
        data: { values: [{ x0: 0, y0: 0, x1: maxAbs, y1: maxAbs }] },
        mark: { type: "rect", color: "#f9edec", opacity: 0.6 },
        encoding: {
          x: { field: "x0", type: "quantitative" },
          x2: { field: "x1" },
          y: { field: "y0", type: "quantitative" },
          y2: { field: "y1" },
        },
      },
      {
        data: { values: [{ v: 0 }] },
        mark: { type: "rule", color: "#d6cfc0" },
        encoding: { x: { field: "v", type: "quantitative" } },
      },
      {
        data: { values: [{ v: 0 }] },
        mark: { type: "rule", color: "#d6cfc0" },
        encoding: { y: { field: "v", type: "quantitative" } },
      },
      {
        data: { values: data },
        mark: { type: "point", filled: true, size: 160, stroke: "#faf8f4", strokeWidth: 1.5 },
        encoding: {
          x: {
            field: "ar1Z",
            type: "quantitative",
            title: "Autocorrelation trend (Mann-Kendall z)",
            scale: { domain: [-maxAbs, maxAbs] },
          },
          y: {
            field: "varZ",
            type: "quantitative",
            title: "Variance trend (z)",
            scale: { domain: [-maxAbs, maxAbs] },
          },
          color: {
            condition: { test: "datum.warning", value: "#b3524d" },
            value: "#4f5d9e",
          },
          tooltip: [
            { field: "topic", type: "nominal", title: "Series" },
            { field: "ar1Z", type: "quantitative", title: "AR1 trend z", format: "+.2f" },
            { field: "ar1P", type: "quantitative", title: "AR1 trend p", format: ".3f" },
            { field: "varZ", type: "quantitative", title: "Variance trend z", format: "+.2f" },
            { field: "varP", type: "quantitative", title: "Variance trend p", format: ".3f" },
            { field: "latestAr1", type: "quantitative", title: "Latest AR1", format: ".2f" },
            { field: "n", type: "quantitative", title: "Sessions" },
            { field: "warning", type: "nominal", title: "Early warning" },
          ],
        },
      },
      {
        data: { values: data },
        mark: { type: "text", dy: -13, fontSize: 11, fontWeight: 600, color: "#2b2b33" },
        encoding: {
          x: { field: "ar1Z", type: "quantitative" },
          y: { field: "varZ", type: "quantitative" },
          text: { field: "topic", type: "nominal" },
        },
      },
      {
        data: { values: [{ x: maxAbs * 0.97, y: maxAbs * 0.95, label: "destabilization zone" }] },
        mark: { type: "text", align: "right", fontSize: 10.5, fontStyle: "italic", color: "#b3524d" },
        encoding: {
          x: { field: "x", type: "quantitative" },
          y: { field: "y", type: "quantitative" },
          text: { field: "label", type: "nominal" },
        },
      },
    ],
    title: undefined,
  };
}

/* ============================================================
   Physio: daily Oura trajectories per metric, with a weekly-mean
   Theil-Sen/Mann-Kendall trend (ISO-week aggregation removes the
   day-to-day autocorrelation that would overstate significance).
   ============================================================ */

/**
 * UTC ISO-8601 year-week key ("YYYY-Www"), Thursday rule: the ISO week
 * belongs to the year containing its Thursday. Written by hand — no
 * date library dependency exists in this codebase for it.
 */
export function isoWeekKey(date: string): string {
  const d = new Date(date + "T00:00:00Z");
  // ISO weekday: Monday=1 .. Sunday=7
  const isoDay = (d.getUTCDay() + 6) % 7 + 1;
  // Move to the Thursday of this ISO week — its calendar year is the ISO year.
  const thursday = new Date(d);
  thursday.setUTCDate(d.getUTCDate() - isoDay + 4);
  const isoYear = thursday.getUTCFullYear();
  // Week 1 is the week containing that year's first Thursday, i.e. the week
  // of Jan 4th.
  const jan4 = new Date(Date.UTC(isoYear, 0, 4));
  const jan4IsoDay = (jan4.getUTCDay() + 6) % 7 + 1;
  const week1Monday = new Date(jan4);
  week1Monday.setUTCDate(jan4.getUTCDate() - jan4IsoDay + 1);
  const weekNum = Math.round((thursday.getTime() - week1Monday.getTime()) / (7 * 86400_000)) + 1;
  return `${isoYear}-W${String(weekNum).padStart(2, "0")}`;
}

export interface PhysioMetric {
  metric: string;
  daily: { date: string; value: number }[];
  rolling: { date: string; value: number }[];   // 7-day centered mean
  trend: { slopePerYear: number; p: number; n: number }; // on ISO-weekly means
}

const PHYSIO_METRICS = ["Sleep", "Readiness", "HRV", "Pulse"];

export function computePhysio(
  ouraRows: OuraDailyRow[],
  sessionDates: string[],
): Analysis<PhysioMetric> {
  if (!ouraRows.length) return { results: [], reason: "missing_inputs", maxN: 0 };
  const byMetric = new Map<string, { date: string; value: number }[]>();
  for (const r of ouraRows) {
    if (!byMetric.has(r.metric)) byMetric.set(r.metric, []);
    byMetric.get(r.metric)!.push({ date: r.date, value: r.value });
  }
  const results: PhysioMetric[] = [];
  for (const metric of PHYSIO_METRICS) {
    const daily = (byMetric.get(metric) || []).sort((a, b) => a.date.localeCompare(b.date));
    if (daily.length < 14) continue; // too short for weekly trend — omit
    // 7-day centered rolling mean (window trimmed at edges)
    const rolling = daily.map((d, i) => {
      const w = daily.slice(Math.max(0, i - 3), i + 4);
      return { date: d.date, value: w.reduce((s, x) => s + x.value, 0) / w.length };
    });
    // ISO-week means → Theil-Sen slope (per week → per year) + Mann-Kendall p.
    // Weekly aggregation removes day-to-day autocorrelation; daily MK would
    // overstate significance.
    const weeks = new Map<string, number[]>();
    for (const d of daily) {
      const key = isoWeekKey(d.date);
      if (!weeks.has(key)) weeks.set(key, []);
      weeks.get(key)!.push(d.value);
    }
    const weekly = Array.from(weeks.entries()).sort(([a], [b]) => a.localeCompare(b))
      .map(([, vs]) => vs.reduce((s, x) => s + x, 0) / vs.length);
    const ts = theilSen(weekly.map((_, i) => i), weekly);
    const mk = mannKendall(weekly);
    results.push({
      metric, daily, rolling,
      trend: { slopePerYear: ts.slope * 52.18, p: mk.p, n: weekly.length },
    });
  }
  if (!results.length) return { results: [], reason: "missing_inputs", maxN: 0 };
  // Analysis<T>.reason is REQUIRED — every sibling sets "ok" on success;
  // omitting it fails tsc.
  return { results, reason: "ok", maxN: results[0].daily.length };
}

/**
 * One vconcat row per present metric: low-opacity daily points + rolling
 * 7-day mean line + faint vertical session-date rules, independent y scales
 * (metrics live on wildly different ranges).
 */
export function buildPhysioSpec(
  data: PhysioMetric[],
  sessionDates: string[],
  dims: ChartDimensions,
) {
  const rowHeight = Math.max(90, Math.floor(dims.height / Math.max(1, data.length)));
  const sessionRuleData = sessionDates.map((date) => ({ date }));

  const rows = data.map((m) => {
    const signed = m.trend.slopePerYear >= 0
      ? `+${m.trend.slopePerYear.toFixed(1)}`
      : m.trend.slopePerYear.toFixed(1);
    const rowTitle = `${m.metric} — ${signed}/yr (MK p=${m.trend.p.toFixed(3)})`;

    return {
      width: dims.width,
      height: rowHeight,
      title: rowTitle,
      layer: [
        {
          data: { values: sessionRuleData },
          transform: [{ calculate: "toDate(datum.date)", as: "dateObj" }],
          mark: { type: "rule", color: "#8a8794", strokeOpacity: 0.25, strokeWidth: 1 },
          encoding: {
            x: { field: "dateObj", type: "temporal", timeUnit: "yearmonthdate", title: null },
          },
        },
        {
          data: { values: m.daily },
          transform: [{ calculate: "toDate(datum.date)", as: "dateObj" }],
          mark: { type: "point", filled: true, size: 18, opacity: 0.25, color: "#4f5d9e" },
          encoding: {
            x: { field: "dateObj", type: "temporal", timeUnit: "yearmonthdate", title: "Date" },
            y: { field: "value", type: "quantitative", title: m.metric },
            tooltip: [
              { field: "value", type: "quantitative", title: m.metric, format: ".1f" },
              { field: "dateObj", type: "temporal", title: "Date" },
            ],
          },
        },
        {
          data: { values: m.rolling },
          transform: [{ calculate: "toDate(datum.date)", as: "dateObj" }],
          mark: { type: "line", interpolate: "monotone", strokeWidth: 2.5, color: "#4f5d9e" },
          encoding: {
            x: { field: "dateObj", type: "temporal", timeUnit: "yearmonthdate", title: "Date" },
            y: { field: "value", type: "quantitative", title: m.metric },
            tooltip: [
              { field: "value", type: "quantitative", title: `${m.metric} (7-day mean)`, format: ".1f" },
              { field: "dateObj", type: "temporal", title: "Date" },
            ],
          },
        },
      ],
      resolve: { scale: { x: "shared" } },
    };
  });

  return {
    ...BASE_VL,
    width: dims.width,
    vconcat: rows,
    resolve: { scale: { y: "independent" } },
  };
}

/* ============================================================
   Temporal theme network: which theme predicts which, one session ahead
   (network theory of psychopathology — directed, FDR-controlled)
   ============================================================ */

export interface NetworkEdge {
  from: string;
  to: string;
  rho: number;
  p: number;
  q: number;
  n: number;
  x: number;
  y: number;
  x2: number;
  y2: number;
  midX: number;
  midY: number;
  angle: number;
  pairLabel: string;
}

export interface NetworkNode {
  topic: string;
  x: number;
  y: number;
  labelX: number;
  labelY: number;
  strength: number;
}

export interface NetworkAnalysis {
  nodes: NetworkNode[];
  edges: NetworkEdge[];
  /** directed pairs tested (for the honest empty-state message) */
  tested: number;
  reason: AnalysisReason;
  maxN: number;
}

/**
 * Directed lagged association: Spearman of Δtopic_A at session t against
 * Δtopic_B at session t+1, permutation p, BH-FDR across all directed pairs.
 * Only FDR-surviving edges are returned. Node strength = Σ|ρ| of outgoing
 * significant edges → the "intervention target" candidate.
 */
export function computeNetwork(rows: ExtractorRow[]): NetworkAnalysis {
  const usable = nonCompositionalRows(rows);
  if (usable.length === 0) {
    return {
      nodes: [], edges: [], tested: 0,
      reason: hasCompositional(rows) ? "compositional_only" : "no_data",
      maxN: 0,
    };
  }

  const series = topicSeries(usable);
  const names = Array.from(series.keys());
  if (names.length < 2) return { nodes: [], edges: [], tested: 0, reason: "no_data", maxN: 0 };

  let maxN = 0;
  const tested: { from: string; to: string; rho: number; p: number; n: number }[] = [];

  for (const from of names) {
    for (const to of names) {
      if (from === to) continue;
      const { xs, ys } = alignByDate(series.get(from)!, series.get(to)!);
      const m = xs.length;
      maxN = Math.max(maxN, m);
      if (m < MIN_SESSIONS + 2) continue;
      const dFrom = firstDifferences(xs);
      const dTo = firstDifferences(ys);
      // Δfrom at t vs Δto at t+1
      const a = dFrom.slice(0, -1);
      const b = dTo.slice(1);
      const { rho, p } = spearmanPermutationP(a, b, PERMUTATIONS, PERM_SEED);
      if (!Number.isFinite(rho)) continue;
      tested.push({ from, to, rho, p, n: m });
    }
  }

  if (tested.length === 0) {
    return { nodes: [], edges: [], tested: 0, reason: "not_enough_sessions", maxN };
  }

  const qvals = benjaminiHochberg(tested.map((t) => t.p));
  const significant = tested
    .map((t, i) => ({ ...t, q: qvals[i] }))
    .filter((t) => t.q < FDR_ALPHA);

  // circular layout
  const R = 1;
  const nodePos = new Map<string, { x: number; y: number }>();
  names.forEach((name, i) => {
    const ang = (2 * Math.PI * i) / names.length - Math.PI / 2;
    nodePos.set(name, { x: R * Math.cos(ang), y: R * Math.sin(ang) });
  });

  const edges: NetworkEdge[] = significant.map((e) => {
    const A = nodePos.get(e.from)!;
    const B = nodePos.get(e.to)!;
    const sx = A.x + 0.16 * (B.x - A.x);
    const sy = A.y + 0.16 * (B.y - A.y);
    const ex = A.x + 0.84 * (B.x - A.x);
    const ey = A.y + 0.84 * (B.y - A.y);
    return {
      from: e.from,
      to: e.to,
      rho: Math.round(e.rho * 100) / 100,
      p: Math.round(e.p * 1000) / 1000,
      q: Math.round(e.q * 1000) / 1000,
      n: e.n,
      x: sx, y: sy, x2: ex, y2: ey,
      midX: A.x + 0.72 * (B.x - A.x),
      midY: A.y + 0.72 * (B.y - A.y),
      angle: Math.round((Math.atan2(-(ey - sy), ex - sx) * 180) / Math.PI),
      pairLabel: `${e.from} → ${e.to}`,
    };
  });

  const strength = new Map<string, number>();
  for (const e of edges) {
    strength.set(e.from, (strength.get(e.from) || 0) + Math.abs(e.rho));
  }

  const nodes: NetworkNode[] = names.map((name) => {
    const pos = nodePos.get(name)!;
    return {
      topic: name,
      x: pos.x,
      y: pos.y,
      labelX: pos.x * 1.24,
      labelY: pos.y * 1.24,
      strength: Math.round((strength.get(name) || 0) * 100) / 100,
    };
  });

  return { nodes, edges, tested: tested.length, reason: "ok", maxN };
}

export function buildNetworkSpec(
  net: NetworkAnalysis,
  dims: ChartDimensions,
  title?: string
) {
  const size = Math.min(dims.width, Math.max(dims.height, 320));
  const DOM = [-1.5, 1.5];

  return {
    ...BASE_VL,
    width: dims.width,
    height: size,
    layer: [
      {
        data: { values: net.edges },
        transform: [{ calculate: "abs(datum.rho)", as: "absRho" }],
        mark: { type: "rule", opacity: 0.85, strokeCap: "round" },
        encoding: {
          x: { field: "x", type: "quantitative", scale: { domain: DOM }, axis: null },
          y: { field: "y", type: "quantitative", scale: { domain: DOM }, axis: null },
          x2: { field: "x2" },
          y2: { field: "y2" },
          color: {
            condition: { test: "datum.rho > 0", value: "#1287ab" },
            value: "#c14a44",
          },
          strokeWidth: {
            field: "absRho",
            type: "quantitative",
            scale: { domain: [0, 1], range: [1.5, 5] },
            legend: null,
          },
          tooltip: [
            { field: "pairLabel", type: "nominal", title: "Direction (this week → next week)" },
            { field: "rho", type: "quantitative", title: "Spearman ρ (lagged, detrended)", format: "+.2f" },
            { field: "q", type: "quantitative", title: "q (FDR-adjusted)", format: ".3f" },
            { field: "n", type: "quantitative", title: "Sessions" },
          ],
        },
      },
      {
        data: { values: net.edges },
        mark: { type: "text", text: "➤", fontSize: 12 },
        encoding: {
          x: { field: "midX", type: "quantitative", scale: { domain: DOM }, axis: null },
          y: { field: "midY", type: "quantitative", scale: { domain: DOM }, axis: null },
          angle: { field: "angle", type: "quantitative", scale: null },
          color: {
            condition: { test: "datum.rho > 0", value: "#1287ab" },
            value: "#c14a44",
          },
        },
      },
      {
        data: { values: net.nodes },
        mark: { type: "point", filled: true, stroke: "#faf8f4", strokeWidth: 2 },
        encoding: {
          x: { field: "x", type: "quantitative", scale: { domain: DOM }, axis: null },
          y: { field: "y", type: "quantitative", scale: { domain: DOM }, axis: null },
          size: {
            field: "strength",
            type: "quantitative",
            scale: { domain: [0, Math.max(1, ...net.nodes.map((n) => n.strength))], range: [200, 750] },
            legend: null,
          },
          color: { value: "#4f5d9e" },
          tooltip: [
            { field: "topic", type: "nominal", title: "Theme" },
            { field: "strength", type: "quantitative", title: "Out-strength (Σ|ρ|) — intervention leverage", format: ".2f" },
          ],
        },
      },
      {
        data: { values: net.nodes },
        mark: { type: "text", fontSize: 11.5, fontWeight: 600, color: "#17171d" },
        encoding: {
          x: { field: "labelX", type: "quantitative", scale: { domain: DOM }, axis: null },
          y: { field: "labelY", type: "quantitative", scale: { domain: DOM }, axis: null },
          text: { field: "topic", type: "nominal" },
        },
      },
    ],
    title: undefined,
  };
}
